// urlOrgSlug.js — which ORG does a shell URL name?
//
// Moved out of app.js (WHITELABEL-STRICT-TENANT-SCOPE-1) so every caller that
// asks this question shares ONE definition: app.js's MWT reconcile and its
// neutral-chrome test (`_foreignOrgUrl`), the custom-domain boot
// (customDomainBoot.js), and the white-label route guard
// (whitelabelRouteGuard.js). A second copy of the reserved list or the canvas
// rule is exactly the drift this file exists to prevent.
//
// Pure path classifiers (Rule 64: reads only). `root` is the shell's root
// STATE, consulted only for the installed module routes.
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'

// Read through the shared package at CALL time (app.js's idiom) — the
// barrel's default is not safe to destructure at module scope.
const moduleRoutesFromRoot = (...args) => _wsShared.functions.moduleRoutesFromRoot(...args)
const shellIsModuleRoute = (...args) => _wsShared.functions.isModuleRoute(...args)
const shellGetWorkspacePrefixSlug = (...args) => _wsShared.functions.getWorkspacePrefixSlug(...args)

// A live PROJECT canvas is `/<owner>/<project>` — exactly two path segments
// whose first isn't a reserved app route. Shared by the prompt's InspectBtn
// and the global Zoombar so both surface only over a real canvas surface
// (2026-06-08 — was duplicated inline in InspectBtn).
export const CANVAS_RESERVED_SEGMENTS = new Set([
  'studio',
  'workspaces',
  'tickets',
  'tasks',
  'task',
  'ticket',
  'chat',
  'meet',
  'calendar',
  'dashboard',
  'admin',
  'marketplace',
  // The one catalogue (decision 7): /apps (Workspace apps) and /apps/store
  // (the App Store tab). Unreserved, /apps/store read as owner "apps" /
  // project "store" — a canvas laid under the header (live on dev,
  // 2026-10-25).
  'apps',
  'notes',
  // Legacy alias — documents→notes rename (2026-08-06 per Nika); keep
  // reserved so a stale /documents/<key> deep link never misreads as an
  // <owner>/<project> canvas pair.
  'documents',
  'settings',
  'profile',
  'system-status',
  'logs',
  'analytics',
  'search',
  'notifications',
  'help',
  'data',
  'feed',
  'user',
  'u',
  'auth',
  // Every other two-segment page the shell serves (pages/index.js). A
  // `/<a>/<b>` page whose first segment is not here reads as a project canvas
  // of owner "<a>": the org reconcile finds no member org of that name and,
  // once its retries run out, rescopes the tab to the workspace home — POB
  // S9's /getting-started/team did that live on dev (W25, 2026-10-30) — and
  // the shell lays the page out full-bleed as a canvas.
  // __tests__/gettingStartedRouteIsNotACanvas.test.js gates the page map
  // against this list, so a new two-segment page cannot miss it again.
  'getting-started',
  // /infra/status (the bare /infra stays the full-bleed deployment canvas
  // through app.js's own _isInfraCanvasPath).
  'infra',
  'resolutions',
  // The public share viewer, /s/<token>. It is chromeless, so its layout does
  // not change; signed in, the reconcile read "s" as an org slug.
  's',
  'workspace-analytics',
  // `w` reserves the multi-workspace-tabs prefix segment (`/w/<org-slug>`) so a
  // workspace URL is never misread as a `<owner>/<project>` canvas pair (§6b).
  'w'
])

// Multi-workspace-tabs: intranet routes may carry a leading `/w/<org-slug>`
// (MULTI_WORKSPACE_TABS.md §6d). Every INTRANET route-kind classifier
// normalizes the path through `stripWorkspacePrefix` first so a prefixed
// `/w/acme/tickets` still resolves to its canvas-kind. The canvas classifiers
// (`/studio`, `/canvas`) are NOT prefixed (§6b) so they read `p` raw — but
// `isProjectCanvasPath` DOES strip, so the workspace home `/w/<slug>`
// (2 segments) isn't false-read as a project canvas (belt-and-suspenders with
// `'w'` now in `CANVAS_RESERVED_SEGMENTS`).
// `root` lets this consult the INSTALLED module/app routes. Without it the
// 2-segments-and-not-reserved heuristic claimed every workspace-app subpage —
// `/manage/members`, `/exams/documents` — as a `<owner>/<project>` canvas, so
// ordinary app pages rendered with canvas chrome (Zoombar, Inspect/Split/
// Sidebar buttons, the full-bleed `data-shell-route="canvas"` layout) and the
// tab titled itself "<org> / Canvas". The reserved list can never cover this:
// it is a fixed list, and an installed app's first segment is whatever the app
// chose. An injected route is definitionally not a project canvas.
export const isProjectCanvasPath = (p, root) => {
  const path = shellStripWorkspacePrefix(p || '')
  const segs = path.split('/').filter(Boolean)
  if (segs.length !== 2 || CANVAS_RESERVED_SEGMENTS.has(segs[0])) return false
  return !shellIsModuleRoute(path, moduleRoutesFromRoot(root))
}

// Which ORG does this URL name, for the shapes that carry NO `/w/<org>` prefix?
// Fed to reconcileMwtOrgFromUrl so those shapes reconcile exactly like a
// `/w/<org>` deep link does (client-only switchOrg, no server mutation).
//
// The bug this closes: `/aihouse/site` names aihouse in its owner segment, but
// the reconcile only ever read the `/w/` prefix, so it fell through to the
// canonicalize branch, which early-returns on any non-intranet route. Boot then
// left the active org at whatever was PERSISTED (Mongo `User.activeOrganization`
// → claims → orgs[0]) — so the org switcher said "Natali" and the navbar showed
// Natali's apps while the canvas rendered an aihouse project. Same for
// `/studio/<owner>`: 'studio' is reserved, so it is neither a project canvas nor
// an intranet route, and nothing read its owner segment either.
//
// Deliberately reuses `isProjectCanvasPath` rather than re-deriving "2 segments,
// first not reserved": that predicate already excludes the reserved chrome routes
// AND the INSTALLED module routes, which is why `/manage/members` and
// `/exams/documents` aren't mistaken for `<owner>/<project>`. A second copy of
// that reserved list is exactly the drift this file removes.
//
// Returns a SLUG only. Whether the user may switch to it is `mwtOrgToReconcile`'s
// call — it returns null for a slug that isn't in the user's own orgs, so opening
// a stranger's public project never attempts a switch.
export const urlOrgSlug = (p, root) => {
  const path = shellStripWorkspacePrefix(p || '')
  const segs = path.split('/').filter(Boolean)
  if (!segs.length) return null
  // `/studio/<owner>` + `/workspaces/<owner>` — org is segment 2 (the same rule
  // studios.js's own ownerSlugFromUrl applies to load that board's data). A
  // bare `/studio` names NO org.
  if (segs[0] === 'studio' || segs[0] === 'workspaces') return segs[1] || null
  // `/<owner>/<key>` — a live project canvas; the owner segment IS the org slug.
  if (isProjectCanvasPath(path, root)) return segs[0] || null
  return null
}

// EVERY shape that names an org: the `/w/<org>` prefix first (the tab's pinned
// scope), then the unprefixed shapes above. '' when the URL names none — a
// bare `/studio`, an app's own sub-page, the root.
export const urlNamedOrgSlug = (p, root) =>
  shellGetWorkspacePrefixSlug(p || '') || urlOrgSlug(p, root) || ''
